import { useCallback, useMemo } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import useBuilderContext from '~/components/page/Automation/v2/components/Builder/hooks/useBuilderContext';
import { actionsSelector } from '~/components/page/Automation/v2/state/actions';
import interactions from '~/components/page/Automation/v2/state/interactions';
import { FlowAction } from '~/graphql/types';
import type { ClientFlowAction } from '~/graphql/types.client';
import isEmptyNode from '../../utils/isEmptyNode';
import resetAndCloneBranch from '../../utils/resetAndCloneBranch';
import useFitSelectableLeafActionsIntoView from '../useFitSelectableLeafActionsIntoView';
import {
  HandlerId,
  PathType,
} from '../../components/nodeTypes/components/IfElseCard/types';
import getActionsOnLowestLevel from '../../utils/getActionsOnLowestLevel';
import resolveConnectionActionId from './utils/resolveConnectionActionId';
import getTargetIdFromSourcePortal, {
  isSourcePortalId,
} from '../../utils/getTargetIdFromSourcePortal';
import { reportAndThrow } from '~/util/reportAndThrow';
import stripInvalidSecondaryIfElseParents from './utils/stripInvalidSecondaryIfElseParents';
import addClonedSecondaryParents from './utils/addClonedSecondaryParents';
import reassignParentId from './utils/reassignParentId';
import assertCopyPhaseTransition from './utils/assertCopyPhaseTransition';

type Params = {
  // parent id where we are pasting
  source: string;

  // child id where we are pasting
  target: string;

  // for the empty nodes in IfElse action, which path we should assign it to
  sourceKey?: HandlerId;

  // Whether external secondary parents may be preserved/propagated for this paste point.
  allowExternalSecondaryParents?: boolean;
};

const usePasteAction = ({
  source,
  target,
  sourceKey,
  allowExternalSecondaryParents = false,
}: Params): {
  onInsertAction: () => void;
} => {
  const { flowBlueprintId, accountId } = useBuilderContext();
  const [currentInteraction, setInteraction] = useRecoilState(interactions);

  const setActions = useSetRecoilState(
    actionsSelector({
      accountId,
      flowBlueprintId,
    }),
  );

  const copiedActions = useMemo(
    () =>
      currentInteraction?.type === 'copyAction'
        ? currentInteraction.copiedActions
        : [],
    [currentInteraction],
  );

  const fitSelectableLeafActionsIntoView =
    useFitSelectableLeafActionsIntoView();

  const onConfirm = useCallback(
    (args?: { leafActionId?: string; ifElsePath?: PathType }) => {
      if (currentInteraction?.type !== 'copyAction') return;
      if (copiedActions.length === 0) return;

      const chosenLeafOriginalId = args?.leafActionId ?? null;
      const chosenIfElsePath = args?.ifElsePath ?? null;

      // In choosingPathToConnect phase the paste location was stored when the
      // user first clicked an edge. Falling back to the current source/target
      // props here would silently paste at the wrong position, so we throw an error
      if (
        currentInteraction.phase === 'choosingPathToConnect' &&
        !currentInteraction.pastePoint
      ) {
        return reportAndThrow(
          'choosingPathToConnect phase reached without a stored pastePoint',
        );
      }

      const pasteSource =
        currentInteraction.phase === 'choosingPathToConnect'
          ? currentInteraction.pastePoint!.source
          : source;
      const pasteTarget =
        currentInteraction.phase === 'choosingPathToConnect'
          ? currentInteraction.pastePoint!.target
          : target;

      const clonedBranch = resetAndCloneBranch({
        branch: copiedActions,
        source: pasteSource,
        preserveInternalSecondaryParents: isEmptyNode(pasteTarget),
      });

      if (clonedBranch.length === 0) return;

      const clonedIdByOriginalId = new Map<string, string>();
      for (const cloned of clonedBranch) {
        if (cloned._copiedFromId)
          clonedIdByOriginalId.set(cloned._copiedFromId, cloned.id);
      }

      const copiedOriginalIds = new Set(copiedActions.map(a => a.id));

      const rootActionId = clonedBranch[0].id;

      if (isEmptyNode(pasteTarget)) {
        // Pasting into an "empty node" means there is no real target action yet.
        // If this empty node is under an IfElse, `sourceKey` tells us which path
        // we should connect the root action to.
        setActions(prevActions => {
          const mutableActions = [...prevActions];
          const sanitizedClonedBranch = stripInvalidSecondaryIfElseParents(
            clonedBranch,
            mutableActions,
          );
          const pastePointIsSourcePortal =
            isSourcePortalId(target) || isSourcePortalId(pasteTarget);

          // Walk upward from pasteSource to collect pasteSource and all its
          // ancestors in one O(m) pass. This serves two purposes:
          // 1. actionsThatWouldOwnPastedBranch — actions that are ancestors of
          //    pasteSource should not receive cloned secondary parents, since they
          //    already "own" the paste point.
          // 2. pasteSourceIsInsideCopiedBranch — if any ancestor is a copied
          //    action, then pasteSource lives inside the copied branch.
          const actionById = new Map(mutableActions.map(a => [a.id, a]));
          const actionsThatWouldOwnPastedBranch = new Set<string>([
            pasteSource,
          ]);
          const ancestorQueue = [pasteSource];
          while (ancestorQueue.length > 0) {
            const id = ancestorQueue.shift()!;
            const action = actionById.get(id);
            if (!action || !('parentIds' in action)) continue;
            for (const parentId of action.parentIds) {
              if (!actionsThatWouldOwnPastedBranch.has(parentId)) {
                actionsThatWouldOwnPastedBranch.add(parentId);
                ancestorQueue.push(parentId);
              }
            }
          }

          const pasteSourceIsInsideCopiedBranch = [
            ...actionsThatWouldOwnPastedBranch,
          ].some(id => copiedOriginalIds.has(id));

          if (sourceKey) {
            const parentActionIndex = mutableActions.findIndex(
              action => action.id === pasteSource,
            );
            const parentAction = mutableActions[parentActionIndex];

            if (
              parentAction &&
              parentAction.__typename === 'FlowV2_Action_IfElse'
            ) {
              const childIdKey = `${sourceKey}Id` as
                | 'trueChildId'
                | 'falseChildId';

              mutableActions[parentActionIndex] = {
                ...parentAction,
                [childIdKey]: rootActionId,
              } as ClientFlowAction;
            }
          }

          const updatedExisting = mutableActions.map(action =>
            !allowExternalSecondaryParents ||
            pastePointIsSourcePortal ||
            pasteSourceIsInsideCopiedBranch ||
            actionsThatWouldOwnPastedBranch.has(action.id)
              ? action
              : addClonedSecondaryParents(
                  action,
                  copiedOriginalIds,
                  clonedIdByOriginalId,
                ),
          );
          return [...updatedExisting, ...sanitizedClonedBranch];
        });

        setInteraction(null);
        return;
      }

      // Non-empty target: we're inserting a copied branch between pasteSource and pasteTarget.
      setActions(prevActions => {
        const prevSource = prevActions.find(a => a.id === pasteSource);
        const existingSecondaryChildren = prevSource?.childrenIds ?? [];

        if (!prevSource) return prevActions;

        const effectivePasteTarget =
          getTargetIdFromSourcePortal(pasteTarget) ?? pasteTarget;

        const sanitizedBranch = stripInvalidSecondaryIfElseParents(
          clonedBranch,
          prevActions,
        );

        // Determine which action should become the new parent of the existing
        // target: either the user's chosen leaf (with optional IfElse path
        // wiring) or the deepest leaf of the cloned branch by default.
        const { connectionActionId, updatedBranch: updatedClonedBranch } =
          resolveConnectionActionId({
            clonedBranch: sanitizedBranch,
            chosenLeafOriginalId,
            chosenIfElsePath,
            effectivePasteTarget,
          });

        if (!connectionActionId) return prevActions;

        return [
          ...prevActions.map(action => {
            let updated: ClientFlowAction = action;

            // Rewire the original edge source -> target into:
            // pasteSource -> rootAction, and connectionLeaf -> pasteTarget.
            if (
              updated.id === pasteSource &&
              updated.__typename === 'FlowV2_Action_IfElse'
            ) {
              const ifElseKey =
                updated.trueChildId === effectivePasteTarget
                  ? 'trueChildId'
                  : updated.falseChildId === effectivePasteTarget
                    ? 'falseChildId'
                    : null;

              if (ifElseKey) {
                updated = {
                  ...updated,
                  [ifElseKey]: rootActionId,
                } as ClientFlowAction;
              }
            }

            // When pasting onto a portal edge, actions that were secondary
            // children of pasteSource (connected via a portal) must have their
            // parent reference updated to point to the pasted branch's leaf
            // instead of pasteSource directly, so the portal relationship
            // threads through the inserted branch rather than skipping it.
            if (
              isSourcePortalId(pasteTarget) &&
              existingSecondaryChildren.includes(updated.id) &&
              'parentIds' in updated
            ) {
              updated = {
                ...updated,
                parentIds: reassignParentId({
                  parentIds: updated.parentIds,
                  previousParentId: pasteSource,
                  nextParentId: connectionActionId,
                }),
              } as ClientFlowAction;
            }

            if (updated.id === effectivePasteTarget && 'parentIds' in updated) {
              updated = {
                ...updated,
                parentIds: reassignParentId({
                  parentIds: updated.parentIds,
                  previousParentId: pasteSource,
                  nextParentId: connectionActionId,
                }),
              } as ClientFlowAction;
            }

            return updated;
          }),
          ...updatedClonedBranch,
        ];
      });

      setInteraction(null);
    },
    [
      allowExternalSecondaryParents,
      copiedActions,
      currentInteraction,
      setActions,
      setInteraction,
      source,
      sourceKey,
      target,
    ],
  );

  const onInsertAction = useCallback(() => {
    if (currentInteraction?.type !== 'copyAction') return;
    if (copiedActions.length === 0) return;

    switch (currentInteraction.phase) {
      case 'pasting': {
        const hasAnyIfElse = copiedActions.some(
          action => action.actionType === FlowAction.IfElse,
        );
        if (hasAnyIfElse && !isEmptyNode(target)) {
          const lowestLevel = getActionsOnLowestLevel(copiedActions);

          // Store the original paste location, then let the user pick a leaf node to connect.
          assertCopyPhaseTransition(
            currentInteraction.phase,
            'choosingPathToConnect',
          );
          setInteraction({
            ...currentInteraction,
            phase: 'choosingPathToConnect',
            pastePoint: { source, target },
          });

          fitSelectableLeafActionsIntoView(lowestLevel.map(a => a.id));
          return;
        }

        // No IfElse in the branch: paste immediately.
        onConfirm();
        return;
      }

      case 'choosingPathToConnect': {
        if (!currentInteraction.pastePoint) return;

        const lowestLevel = getActionsOnLowestLevel(copiedActions);
        const leafIds = new Set(lowestLevel.map(a => a.id));
        const chosenLeaf = copiedActions.find(a => a.id === source) ?? null;
        const copiedActionIds = new Set(copiedActions.map(a => a.id));
        // An IfElse node is not a topological leaf (it has children), so it
        // won't appear in leafIds. However, if the user clicks one of its paths
        // that has no in-branch child — either the path is empty or its child
        // is outside the copied branch — that path is a valid connection point.
        // isOpenIfElsePathChoice captures this case so the guard below doesn't
        // reject the click.
        const isOpenIfElsePathChoice =
          chosenLeaf?.actionType === FlowAction.IfElse &&
          sourceKey != null &&
          chosenLeaf.__typename === 'FlowV2_Action_IfElse' &&
          ((sourceKey === HandlerId.trueChild &&
            (!chosenLeaf.trueChildId ||
              !copiedActionIds.has(chosenLeaf.trueChildId))) ||
            (sourceKey === HandlerId.falseChild &&
              (!chosenLeaf.falseChildId ||
                !copiedActionIds.has(chosenLeaf.falseChildId))));

        if (!leafIds.has(source) && !isOpenIfElsePathChoice) return;

        const chosenPath = (() => {
          if (!chosenLeaf) {
            throw new Error('No leaf chosen for branch');
          }

          if (chosenLeaf.actionType === FlowAction.IfElse) {
            if (!sourceKey) {
              throw new Error('No source key chosen for if else');
            }

            switch (sourceKey) {
              case HandlerId.falseChild:
                return PathType.False;
              case HandlerId.trueChild:
                return PathType.True;
              default:
                throw new Error('Invalid source key for if else');
            }
          }

          return null;
        })();

        onConfirm({
          leafActionId: source,
          ifElsePath: chosenPath ?? undefined,
        });
        return;
      }
      default:
        return;
    }
  }, [
    copiedActions,
    currentInteraction,
    fitSelectableLeafActionsIntoView,
    onConfirm,
    setInteraction,
    source,
    sourceKey,
    target,
  ]);

  return { onInsertAction };
};

export default usePasteAction;
